import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useHub } from '../context';
import { countUnder, readPins, RecordRow } from '../records';
import { Bar, Card, Check, Counting, FillRow, Heading, Labelled, Message, Pic, SmallButton } from '../ui';

/** Port of the app's Triumphs tab: Active and Legacy (front page, categories, titles), Explorer and Journey. */
export default function TriumphsTab({ sub }: { sub: string }) {
  const h = useHub();
  if (!h.defs) return <Message title="Triumphs" text="Reading triumphs from Bungie's database (once per Destiny update)..." />;
  if (!h.hub!.hasRecords) return <Message title="Triumphs are hidden" text="This player's Bungie privacy settings hide their triumphs." />;
  if (sub === 'Explorer') return <Explorer />;
  if (sub === 'Journey') return <Journey />;
  return <Browse legacy={sub === 'Legacy'} />;
}

function useCount() {
  const { defs, hub, char } = useHub();
  return (node: number) => countUnder(defs!, hub!, char?.id, node);
}

/** Active / Legacy: the three-column front, then categories down to single triumphs, a search, and the titles. */
function Browse({ legacy }: { legacy: boolean }) {
  const h = useHub();
  const defs = h.defs!;
  const count = useCount();
  const live = (r: number) => h.hub!.record(r, h.char?.id);
  const [path, setPath] = useState<number[]>([]);
  // ?seal=<node>: open that title straight away (Legend's title medals link here)
  const [params] = useSearchParams();
  const [seal, setSeal] = useState(() => Number(params.get('seal')) || 0);
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState('');
  const [hideDone, setHideDone] = useState(false);
  const [, bump] = useState(0);
  const root = legacy ? defs.roots.legacy : defs.roots.active;
  const top = () => window.scrollTo({ top: 0 });

  if (seal) return <SealPage seal={seal} back={() => setSeal(0)} />;

  if (path.length === 0 && search.length < 2) {
    // the front: what this page is and the scores, the categories and titles, what is near
    const all = new Set<number>();
    const walk = (n: number) => { const d = defs.nodes.get(n); if (!d) return; d.records.forEach((r) => all.add(r)); d.nodes.forEach(walk); };
    walk(root);
    const open = [...all].map((hash) => ({ hash, def: defs.records.get(hash), l: live(hash) })).filter((x) => x.l && !x.l.done && !x.l.hidden);
    const waiting = open.reduce((s, x) => s + (x.def?.score ?? 0), 0);
    const cats = (defs.nodes.get(root)?.nodes ?? []).filter((n) => defs.nodes.has(n) && count(n).total > 0);
    const sealRoot = legacy ? defs.roots.legacySeals : defs.roots.seals;
    const seals = (defs.nodes.get(sealRoot)?.nodes ?? []).filter((s) => defs.nodes.has(s) && count(s).total > 0);
    const near = open.filter((x) => !x.l!.secret && !x.l!.locked && x.l!.fraction > 0).sort((a, b) => b.l!.fraction - a.l!.fraction);
    const tracked = readPins().filter((p) => defs.records.has(p));
    const all_ = count(root);
    const titles = seals.length > 0 && (
      <Labelled label={legacy ? 'Legacy Titles' : 'Titles'} count={`${seals.filter((s) => live(defs.nodes.get(s)!.seal)?.done).length} / ${seals.length}`} style={{ marginTop: legacy ? 0 : 26 }}>
        {seals.map((s) => {
          const node = defs.nodes.get(s)!;
          const c = count(s);
          return <FillRow key={s} icon={node.icon} name={defs.records.get(node.seal)?.title || node.name} done={c.done} total={c.total}
            tone={live(node.seal)?.done ? 'earned' : 'plain'} onClick={() => { setSeal(s); top(); }} />;
        })}
      </Labelled>
    );
    return (
      <div className="g-tri">
        <div className="g-stack">
          <h1 className="g-title light">{legacy ? 'Legacy Triumphs and Titles' : 'Active Triumphs and Titles'}</h1>
          <p className="g-text2 big14">{legacy ? 'Triumphs from the past. These records can no longer be earned, but are kept here to look back on.'
            : "Records of the Guardian's achievements. Challenge yourself according to the objectives found herein for fame, glory, and prizes."}</p>
          <p className="g-text2 big14">The Guardian has <b className="white">{waiting.toLocaleString()}</b> score waiting to be unlocked.</p>
          <div className="g-scores">
            {[[h.hub!.lifetimeScore, 'Lifetime Triumph Score'], [h.hub!.activeScore, 'Active Triumph Score'], [h.hub!.legacyScore, 'Legacy Triumph Score']].map(([v, l]) => (
              <div key={l}><div className="g-score"><Counting value={v as number} /></div><div className="g-text2 small">{l}</div></div>
            ))}
          </div>
          <input className="g-input" placeholder="<enter name or description>" value={draft} onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && setSearch(draft.trim())} aria-label="Search triumphs" />
        </div>
        <div className="g-stack tight">
          <Labelled label={legacy ? 'Legacy Triumphs' : 'Triumphs'} count={`${all_.done} / ${all_.total}`}>
            {cats.map((k) => {
              const c = count(k);
              const n = defs.nodes.get(k)!;
              return <FillRow key={k} icon={n.icon} name={n.name} done={c.done} total={c.total} onClick={() => { setPath([k]); top(); }} />;
            })}
          </Labelled>
          {!legacy && titles}
        </div>
        <div className="g-stack big">
          {legacy ? titles : (
            <>
              {tracked.length > 0 && (
                <Labelled label="Tracked" count={String(tracked.length)}>
                  {tracked.map((r) => <RecordRow key={r} hash={r} onPin={() => bump((x) => x + 1)} />)}
                </Labelled>
              )}
              <Labelled label="Near" count={`${near.length} partially completed`}>
                {near.slice(0, 14).map((x) => <RecordRow key={x.hash} hash={x.hash} onPin={() => bump((n) => n + 1)} />)}
              </Labelled>
            </>
          )}
        </div>
      </div>
    );
  }

  const here = path.length ? path[path.length - 1] : root;
  const node = defs.nodes.get(here);
  const tools = (
    <div className="g-tools">
      <input className="g-input" style={{ width: 260 }} placeholder="Search triumphs" value={draft} onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && setSearch(draft.trim())} onBlur={() => setSearch(draft.trim())} aria-label="Search triumphs" />
      {search.length > 1 && <SmallButton onClick={() => { setSearch(''); setDraft(''); }}>Clear search</SmallButton>}
      <Check label="Hide completed" on={hideDone} set={setHideDone} />
    </div>
  );
  if (search.length > 1) {
    const q = search.toLowerCase();
    const hits = [...defs.records.entries()].filter(([k, r]) => (r.name.toLowerCase().includes(q) || r.desc.toLowerCase().includes(q)) && (() => { const l = live(k); return l && !l.hidden && !l.secret && !(hideDone && l.done); })())
      .map(([k]) => k).slice(0, 60);
    return (
      <>
        {tools}
        <Heading>{hits.length === 60 ? 'First 60 matches' : `${hits.length} match(es) for "${search}"`}</Heading>
        {hits.map((r) => <RecordRow key={r} hash={r} />)}
      </>
    );
  }
  const kids = (node?.nodes ?? []).filter((n) => defs.nodes.has(n) && count(n).total > 0);
  const shown = (node?.records ?? []).filter((r) => { const l = live(r); return l && !l.hidden && !(hideDone && l.done); });
  return (
    <>
      {tools}
      <div className="g-crumbs">
        {[root, ...path].map((n, i) => (
          <span key={i}>
            {i > 0 && <span className="g-dim">/</span>}
            <button type="button" className={i === path.length ? 'on' : ''} onClick={() => setPath(path.slice(0, i))}>
              {i === 0 ? (legacy ? 'Legacy Triumphs' : 'Triumphs') : defs.nodes.get(n)?.name ?? '?'}
            </button>
          </span>
        ))}
      </div>
      {kids.length > 0 && (
        <div className="g-wrap">
          {kids.map((k) => {
            const c = count(k);
            const n = defs.nodes.get(k)!;
            return (
              <button key={k} type="button" className="g-card g-nodetile" onClick={() => { setPath([...path, k]); top(); }}>
                <Pic src={n.icon} width={44} height={44} />
                <span className="g-nodetile-words">
                  <b>{n.name}</b>
                  <Bar fraction={c.total ? c.done / c.total : 0} color={c.done === c.total ? 'var(--good)' : '#ffffff'} />
                  <span className={c.done === c.total ? 'good small' : 'g-text2 small'}>{c.done} / {c.total}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
      {shown.map((r) => <RecordRow key={r} hash={r} />)}
      {kids.length === 0 && shown.length === 0 && <p className="g-text2">{hideDone ? 'Everything here is completed.' : 'Nothing to show here.'}</p>}
    </>
  );
}

/** One title: its seal, what it takes, and each triumph; the gilding ones after. */
export function SealPage({ seal, back }: { seal: number; back: () => void }) {
  const h = useHub();
  const defs = h.defs!;
  const count = useCount();
  const live = (r: number) => h.hub!.record(r, h.char?.id);
  const open = defs.nodes.get(seal);
  if (!open) return null;
  const rec = defs.records.get(open.seal);
  const earned = !!live(open.seal)?.done;
  const c = count(seal);
  const need = open.records.filter((r) => live(r) && !live(r)!.hidden && defs.records.get(r) && !defs.records.get(r)!.gilding);
  const gild = open.records.filter((r) => live(r) && !live(r)!.hidden && defs.records.get(r)?.gilding);
  return (
    <>
      <div><SmallButton onClick={back}>‹   Back to Triumphs</SmallButton></div>
      <Card className="g-sealhead">
        <Pic src={open.icon} width={92} height={92} />
        <div>
          <div className="g-sealtitle" style={{ color: earned ? 'var(--accent-text)' : '#fff' }}>{rec?.title || open.name}</div>
          <div className="g-text2">{open.name}{earned ? '  |  title earned' : ''}</div>
          <p className="g-text2">{open.desc}</p>
          <b className={c.done === c.total ? 'good' : ''}>{c.done} / {c.total} triumphs</b>
        </div>
      </Card>
      {[...need].sort((a, b) => Number(!!live(a)?.done) - Number(!!live(b)?.done)).map((r) => <RecordRow key={r} hash={r} />)}
      {gild.length > 0 && <Heading>Gilding (after the title is earned)</Heading>}
      {gild.map((r) => <RecordRow key={r} hash={r} />)}
    </>
  );
}

// ---------------- Explorer: the whole tree, opened node by node ----------------

function Explorer() {
  const h = useHub();
  const defs = h.defs!;
  const count = useCount();
  const live = (r: number) => h.hub!.record(r, h.char?.id);
  const roots: [string, number][] = [['Triumphs', defs.roots.active], ['Legacy Triumphs', defs.roots.legacy], ['Titles', defs.roots.seals], ['Legacy Titles', defs.roots.legacySeals]];
  const [which, setWhich] = useState(0);
  const [open, setOpen] = useState<Set<number>>(new Set());
  const [flat, setFlat] = useState(false);
  const [hide, setHide] = useState(false);
  const [score, setScore] = useState(false);
  const shown = (r: number) => { const l = live(r); return !!l && !l.hidden && !(hide && l.done) && !(score && !(defs.records.get(r)?.score ?? 0)); };
  const toggle = (n: number) => setOpen((s) => { const x = new Set(s); if (!x.delete(n)) x.add(n); return x; });
  const rows: React.ReactNode[] = [];
  const line = (r: number, depth: number) => {
    const def = defs.records.get(r);
    const l = live(r)!;
    const secret = l.secret && !l.done;
    rows.push(
      <div key={`r${r}:${depth}:${rows.length}`} className="g-exline" style={{ marginLeft: (depth + 1) * 34 }}>
        <Pic src={secret ? undefined : def?.icon} width={18} height={18} />
        <span title={secret ? 'Keep playing to uncover this triumph.' : def?.desc}>{secret ? 'Secret triumph' : def?.name ?? '?'}</span>
        <span className="g-text2">{def?.score ?? 0}</span>
        <span className={`g-exbox ${l.done ? 'done' : ''}`} />
      </div>,
    );
  };
  const add = (hash: number, depth: number) => {
    const node = defs.nodes.get(hash);
    if (!node) return;
    const c = count(hash);
    if (!c.total) return;
    const isOpen = open.has(hash);
    rows.push(
      <button key={`n${hash}`} type="button" className="g-exnode" style={{ marginLeft: depth * 34 }} onClick={() => toggle(hash)}>
        <Pic src={node.icon} width={22} height={22} />
        <span className={isOpen ? 'open' : ''}>{node.name}</span>
        <span className={c.done === c.total ? 'good' : 'g-text2'}>{c.done} / {c.total}</span>
      </button>,
    );
    if (!isOpen) return;
    node.nodes.forEach((k) => add(k, depth + 1));
    node.records.filter(shown).forEach((r) => line(r, depth));
  };
  const rootHash = roots[which][1];
  if (flat) {
    // every triumph under the tree, each once, in the tree's own order (the first 600: a longer list would crawl)
    const seen = new Set<number>();
    const gather = (n: number) => { const d = defs.nodes.get(n); if (!d) return; d.nodes.forEach(gather); d.records.forEach((r) => shown(r) && seen.add(r)); };
    gather(rootHash);
    rows.push(<p key="count" className="g-dim small">{seen.size > 600 ? `${seen.size.toLocaleString()} triumphs; the first 600 are listed. Tick Hide Completed or Grants Score to narrow it.` : `${seen.size.toLocaleString()} triumphs.`}</p>);
    [...seen].slice(0, 600).forEach((r) => line(r, -1));
  } else (defs.nodes.get(rootHash)?.nodes ?? []).forEach((c) => add(c, 0));
  const expandAll = () => {
    const all = new Set<number>();
    const walk = (n: number) => { const d = defs.nodes.get(n); if (!d) return; all.add(n); d.nodes.forEach(walk); };
    walk(rootHash);
    setOpen(all);
  };
  return (
    <div className="g-explorer">
      <div className="g-stack">
        <h1 className="g-title light">Explorer</h1>
        <p className="g-text2 big14">A structured look into your Triumphs. Open and close categories to follow them from the broad pursuits down to the single feats. Every tick box is one triumph.</p>
        <select className="g-input g-select" value={which} onChange={(e) => { setWhich(Number(e.target.value)); setOpen(new Set()); }} aria-label="Tree to explore">
          {roots.map(([n], i) => <option key={n} value={i}>{n}</option>)}
        </select>
        <p className="g-dim small">Pick which tree to explore.</p>
        <Check label="Flatten" hint="One long list of every triumph in the tree, without the categories." on={flat} set={setFlat} />
        <Check label="Hide Completed" hint="Hide the triumphs that are already done." on={hide} set={setHide} />
        <Check label="Grants Score" hint="Only show triumphs that give score when completed." on={score} set={setScore} />
        <div className="g-tools"><SmallButton onClick={() => setOpen(new Set())}>Collapse All</SmallButton><SmallButton onClick={expandAll}>Expand All</SmallButton></div>
      </div>
      <div className="g-extree">
        {!flat && open.size === 0 && <p className="g-dim small">Click a category to open it.</p>}
        {rows}
      </div>
    </div>
  );
}

// ---------------- Journey: the Guardian Ranks ----------------

function Journey() {
  const h = useHub();
  const defs = h.defs!;
  const count = useCount();
  const live = (r: number) => h.hub!.record(r, h.char?.id);
  const current = h.hub!.guardianRank;
  const max = defs.ranks.length ? Math.max(...defs.ranks.map((r) => r.number)) : 0;
  const min = defs.ranks.length ? Math.min(...defs.ranks.map((r) => r.number)) : 0;
  const [pick, setPick] = useState(Math.min(max, current + 1));
  const [cat, setCat] = useState(0);
  if (!defs.ranks.length) return <Message title="Journey" text="The Guardian Ranks could not be read from Bungie's database." />;
  const sel = defs.ranks.find((r) => r.number === pick) ?? defs.ranks[defs.ranks.length - 1];
  const selReached = sel.number <= current;
  const s = count(sel.node);
  const now = defs.ranks.find((r) => r.number === current);
  const cats = (defs.nodes.get(sel.node)?.nodes ?? []).filter((c) => defs.nodes.has(c) && count(c).total > 0);
  const openCat = cats.includes(cat) ? cat : 0;
  const records: number[] = [];
  const collect = (n: number) => { const d = defs.nodes.get(n); if (!d) return; records.push(...d.records); d.nodes.forEach(collect); };
  collect(openCat || sel.node);
  const list = [...new Set(records)].filter((r) => live(r) && !live(r)!.hidden).sort((a, b) => Number(!!live(a)!.done) - Number(!!live(b)!.done));
  return (
    <>
      <div className="g-journey">
        <div className="g-journey-top">
          <Medal n={sel.number} size={104} reached={selReached} held={sel.number === current} goal={sel.number === current + 1} hero />
          <div>
            <div className="g-caps-label">{selReached ? (sel.number === current ? 'GUARDIAN RANK  ·  YOUR RANK NOW' : 'GUARDIAN RANK  ·  REACHED') : sel.number === current + 1 ? 'GUARDIAN RANK  ·  NEXT TO EARN' : 'GUARDIAN RANK'}</div>
            <div className="g-journey-name" style={{ color: selReached ? 'var(--rank-gold)' : 'var(--text)' }}>{sel.name}</div>
            <p className="g-text2 big14">{selReached ? `All ${s.total} challenges of this rank are done.` : `${s.done} of ${s.total} challenges done  ·  ${s.total - s.done} to go`}</p>
            {!selReached && <div style={{ width: 360, maxWidth: '100%', marginTop: 8 }}><Bar fraction={s.total ? s.done / s.total : 0} color="var(--accent-text)" height={6} /></div>}
          </div>
          <div className="g-journey-held">
            <div className="g-caps-label">CURRENT RANK</div>
            <div className="g-journey-heldname">{current}  {now?.name}</div>
            <div className="g-text3 small">of {max} ranks</div>
          </div>
        </div>
        <div className="g-medals">
          {defs.ranks.map((r) => {
            const reached = r.number <= current, goal = r.number === current + 1;
            const c = count(r.node);
            const fill = reached ? 1 : goal && c.total ? c.done / c.total : 0;
            return (
              <button key={r.number} type="button" className="g-medalcell" title={`Rank ${r.number}: ${r.name}${reached ? '  |  reached' : `  |  ${c.done} / ${c.total} challenges`}`}
                onClick={() => { setPick(r.number); setCat(0); }}>
                {r.number > min && <span className="g-track-left"><span style={{ width: `${fill * 100}%`, background: reached ? 'var(--rank-gold)' : 'var(--accent-text)' }} /></span>}
                {r.number < max && <span className="g-track-right" style={{ background: r.number < current ? 'var(--rank-gold)' : 'rgba(255,255,255,0.15)' }} />}
                <Medal n={r.number} size={52} reached={reached} held={r.number === current} goal={goal} picked={r.number === sel.number} />
                <span className={`g-medal-label ${r.number === sel.number ? 'on' : reached ? 'reached' : ''}`}>{r.name}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div className="g-journey-grid">
        <div className="g-stack tight">
          <FillRow name="All Challenges" done={s.done} total={s.total} tone={openCat === 0 ? 'chosen' : 'dim'} onClick={() => setCat(0)} />
          <div style={{ height: 18 }} />
          {cats.map((c) => {
            const x = count(c);
            return <FillRow key={c} name={defs.nodes.get(c)!.name} done={x.done} total={x.total} tone={openCat === c ? 'chosen' : x.done === x.total ? 'gold' : 'dim'} onClick={() => setCat(c)} />;
          })}
        </div>
        {list.length === 0 ? <p className="g-text2">No challenges to show for this rank.</p> : (
          <div className="g-cols two">
            <div className="g-stack">{list.filter((_, i) => i % 2 === 0).map((r) => <RecordRow key={r} hash={r} />)}</div>
            <div className="g-stack">{list.filter((_, i) => i % 2 === 1).map((r) => <RecordRow key={r} hash={r} />)}</div>
          </div>
        )}
      </div>
    </>
  );
}

/** A rank as a medal: a gold rim round a dark face when reached, a dashed ring when it is the goal, a faint ring when it waits. */
function Medal({ n, size, reached, held, goal, picked, hero }: { n: number; size: number; reached: boolean; held?: boolean; goal?: boolean; picked?: boolean; hero?: boolean }) {
  return (
    <span className={`g-medal ${reached ? 'reached' : goal ? 'goal' : ''} ${hero ? 'hero' : ''}`} style={{ width: size, height: size, fontSize: size * 0.4, ['--s' as string]: `${size}px` }}>
      {(held || picked) && <span className={`halo ${held ? 'gold' : ''}`} />}
      {reached && <><span className="face" /><span className="ink" /><span className="dots" /><span className="glance"><i /></span></>}
      <b>{n}</b>
      {picked && <span className="pick" />}
    </span>
  );
}

